import { FieldType } from './field-type';
import { FieldOptionRef } from './options-ref';
import { FieldProperties } from './field-properties';
import { FieldOption } from './field-option';
import { DateFieldConfig } from './datefield-config';
import { BehaviorSubject } from 'rxjs/Rx';
import { MaterialDateFieldConfig } from './material-date-field-config';
import { BeanConfig } from './bean-config';
import { PredefinedValue } from '../../core/services/impl/section-services/predefined-values/predefined-values.service';
import { EventEmitter } from '@angular/core';
import {MatFormFieldAppearance} from '@angular/material';
import {SelectDialogMode} from './select-dialog-mode.enum';

export interface MaskConfigModel {
  mask?: string;
  patterns?: any;
  dropSpecialCharacters?: boolean;
  sufix?: string;
}

export interface CheckboxConfig {
  readonly?: boolean,
  expand?: {
    expandedLabel: string, // label for button when additional content is hidden
    collapsedLabel: string, // label for button when additional content is visible
    expandText: string, // additional content (hidden by default)
    inline?: boolean, // additional content will be displayed in the checkbox (under checkbox by the default)
  },
  mappingConfig?: {
    propertyName: string, // property containing current (custom) value
    valuesMapping: {
      propertyValue: any; // value inserted in propertyName when checkbox changes value to given checkboxValue
      checkboxValue: boolean; // value of checkbox for given propertyValue (true or false)
    }[]
  },
  icon?: string; // path to icon shown before caption
  iconChecked?: string; // path to icon shown before caption when checkbox is checked
  disableVisualChangesOnDisabled?: boolean; // if true, disabled checkbox will be visually shown as enabled
  clickableLabel?: boolean; // if true, label click toggles checkbox value
  translateLabel?: boolean
}

export interface SelectConfig {

  dataType?: 'promise' | 'array',
  loadingEvent?: BehaviorSubject<any>,
  searchEvent?: EventEmitter<string>,
  closeEvent?: EventEmitter<any>,
  valueSelectedEvent?: EventEmitter<any>, // used for shared select dialog fields instead of dialog's afterClosed

  async?: boolean,
  loading?: boolean,
  multiple?: boolean,
  closeOnSelect?: boolean,
  clearable?: boolean,
  searchable?: boolean,
  contentText?: string,
  notFoundText?: string,
  submitButton?: boolean,
  clearOnClose?: boolean,
  contentTextType?: string,
  dropdownPosition?: 'bottom' | 'auto' | 'top',
  capitalize?: boolean,
  setValueIfOnlyOneItem?: boolean,
  setValueIfOnlyOneItemTimeout?: number,
  dynamicDictionary?: boolean,
  valueBasedInitialDictionary?: boolean,
  mobileSelectPanelClass?: string,
  typeToSearchText?: string,
  animationEnabled?: boolean,
  autoSelectOnSearch?: boolean, // default - false, value will be automatically selected if user typed full label while searching
  sharedSelectDialog?: boolean, // default - false, allows to shared select dialog among many fields
  lastFieldInSharedSelectDialog?: boolean,
  dialogMode?: SelectDialogMode,
  customIcon?: string,
}

export interface NumberStepInputConfig {
  step: number;
  inputSuffix?: string;
}

export interface FieldConfig {
  items?: any[]
  mask?: string;
  type: FieldType;
  caption?: string;
  captionCount?: number; // used to concatenate with caption
  captionForPreview?: string;
  captionTarget?: FieldCaptionTarget; // field's caption will be used for input's placeholder or mat-form-field's label
  fieldKey: string;
  required?: boolean;
  readonly?: boolean;
  disabled?: boolean;
  validators?: any[];
  initialValue?: any; // value to set in control after component init (control's default value)
  dateFormat?: string;
  description?: string;
  tooltipIconOutlined?: boolean; // defines tooltip icon style - not outlined by default
  tooltipTheme?: string;
  autocomplete?: string;
  captionAsHTML?: string;
  inputDisabled?: boolean;
  longDescription?: string;
  relatedFields?: string[];
  calculateOnInit?: boolean;
  captionAdditional?: string;
  options?: FieldOption | {};
  optionsRef?: FieldOptionRef;
  onChangeListeners?: boolean;
  maskConfig?: MaskConfigModel;
  properties?: FieldProperties;
  responsiveStyleConfig?: string;
  captionAdditionalTrue?: string;
  captionAdditionalFalse?: string;
  materialDateFieldConfig?: MaterialDateFieldConfig
  fieldsDataFormat?: 'objectArray' | 'array' | 'string';
  convertFormat?: 'string' | 'number';
  maxlength?: number;
  predefined?: PredefinedValue;
  personalData?: boolean;
  appearance?: MatFormFieldAppearance;
  prefixIcon?: string;
  outsidePrefixIcon?: string;
  suffixIcon?: string;

  beanConfig?: BeanConfig;
  checkboxConfig?: CheckboxConfig;
  dateFieldConfig?: DateFieldConfig;
  radioConfig?: RadioOptionConfig[];
  radioCheckboxTileConfig?: RadioCheckboxTileConfig;
  radioInline?: boolean;
  tilesMode?: boolean;
  select?: SelectConfig;
  numberStepInputConfig?: NumberStepInputConfig;

  displayInEditableMode?: boolean; // czy pole ma byc do edycji pod trybem podgladowym
  displayInPreviewMode?: boolean; // czy pole ma byc prezentowane w trybie podgladowym
  fullWidthInPreviewMode?: boolean; // czy pole ma zajmować całą szerokość w trybie poglądowym

  analyticsConfig?: FieldAnalyticsConfig;
}

export interface RadioOptionConfig {
  value: any;
  label: string;
}

export interface RadioCheckboxTileConfig {
  checkboxDisabled?: boolean;
  contentDataValuePropertyName?: string;
  getAnalyticsFieldValue?: (itemData: any) => any;
}

export interface FieldAnalyticsConfig {
  eventsToSkip?: string[]; // list of GA4 event keys to exclude from sending
  fieldNameSource?: FieldNameSource; // allows to overwrite checkbox's field name mechanism which - by default - treats fieldKey as fieldName in event
  customFieldName?: string; // allows to overwrite field name
  customFieldValue?: () => any;
  includeFieldValue?: boolean; // allows to overwrite text & textarea fields behaviour where passing field value in field click event is disabled
  skipFieldValue?: boolean; // allows to disable passing field value to field click event (for fields other than text)
}

export enum FieldNameSource {
  CAPTION = 'CAPTION',
  SELECT_CONTENT_TEXT = 'SELECT_CONTENT_TEXT',
}

export enum FieldCaptionTarget {
  PLACEHOLDER = 'PLACEHOLDER',
  LABEL = 'LABEL',
}
